import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { MAIN_NAV, type NavCategory, type NavSection, type NavCta } from "~/config/navigation";
import { ChevronDown, ArrowRight } from "~/components/icons";

function SectionLabel({ section }: { section: NavSection }) {
  const emphasis = section.labelEmphasis;
  const emphasisIndex = emphasis ? section.label.indexOf(emphasis) : -1;

  if (!emphasis || emphasisIndex < 0) return section.label;

  return (
    <>
      {section.label.slice(0, emphasisIndex)}
      <strong className="font-bold">{emphasis}</strong>
      {section.label.slice(emphasisIndex + emphasis.length)}
    </>
  );
}

/**
 * Desktop mega-menu navigation using Radix NavigationMenu.
 *
 * Content renders directly below each trigger (no Viewport portaling).
 * This avoids positioning issues with absolutely-positioned roots.
 *
 * Supports dark mode via Tailwind v4 `dark:` variant.
 */
export function MegaMenu({ isEngineering = false }: { isEngineering?: boolean }) {
  const darkHover = isEngineering
    ? "dark:hover:bg-[rgba(0,255,204,0.1)]"
    : "dark:hover:bg-white/10";
  const darkOpen = isEngineering
    ? "dark:data-[state=open]:bg-[rgba(0,255,204,0.1)]"
    : "dark:data-[state=open]:bg-white/10";
  // Subtle accent shift for item labels on hover within the dropdown.
  const darkItemHover = isEngineering
    ? "dark:group-hover:text-eng-electric"
    : "dark:group-hover:text-white";

  return (
    <NavigationMenu.Root
      className="pointer-events-none hidden lg:absolute lg:inset-0 lg:block"
      delayDuration={200}
      skipDelayDuration={300}
    >
      <NavigationMenu.List className="flex h-16 items-center justify-start pl-[101px] text-sm">
        {MAIN_NAV.map((cat) =>
          cat.sections ? (
            <NavigationMenu.Item key={cat.id} className="static">
              <NavigationMenu.Trigger
                className={`pointer-events-auto group flex items-center gap-1.5 rounded-lg px-3 py-3 font-body text-sm font-semibold leading-[14px] text-nav-text tracking-[-0.01em] transition-all ease-in-out hover:bg-teal-dark/5 data-[state=open]:bg-teal-dark/5 dark:text-white ${darkHover} ${darkOpen}`}
              >
                <span>{cat.label}</span>
                <ChevronDown className="w-2 text-nav-text transition-transform duration-200 group-data-[state=open]:rotate-180 dark:text-white" />
              </NavigationMenu.Trigger>
              <NavigationMenu.Content
                className="pointer-events-auto absolute left-0 right-0 top-full z-50"
              >
                {/*
                 * clip-path crops the shadow at the top edge of the dropdown so
                 * the soft drop-shadow doesn't bleed upward into the navbar.
                 * Sides + bottom extend far enough to never clip the visible
                 * shadow falloff (40px blur on the box-shadow).
                 */}
                <div className="bg-white shadow-[0_4px_40px_rgba(0,0,0,0.12)] [clip-path:inset(0_-100px_-100px_-100px)] dark:border-x dark:border-b dark:border-eng-circuit dark:bg-eng-terminal dark:shadow-[0_4px_40px_rgba(0,0,0,0.4)]">
                  <DropdownPanel category={cat} darkItemHover={darkItemHover} />
                </div>
              </NavigationMenu.Content>
            </NavigationMenu.Item>
          ) : (
            <NavigationMenu.Item key={cat.id}>
              <NavigationMenu.Link asChild>
                <a
                  href={cat.url}
                  className={`pointer-events-auto flex items-center rounded-lg px-3 py-3 font-body text-sm font-semibold leading-[14px] text-nav-text tracking-[-0.01em] transition-all ease-in-out hover:bg-teal-dark/5 dark:text-white ${darkHover}`}
                >
                  {cat.label}
                </a>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          ),
        )}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
}

/**
 * Group sections into visual columns based on `section.column`. Sections without
 * an explicit column are placed in sequential standalone columns. Sections sharing
 * a column number stack vertically with a divider between them.
 */
function groupByColumn(sections: NavSection[]): NavSection[][] {
  const groups = new Map<number, NavSection[]>();
  let autoColumn = 1000;
  for (const section of sections) {
    const col = section.column ?? autoColumn++;
    const existing = groups.get(col);
    if (existing) existing.push(section);
    else groups.set(col, [section]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([, list]) => list);
}

function DropdownPanel({
  category,
  darkItemHover,
}: {
  category: NavCategory;
  darkItemHover: string;
}) {
  const columns = category.sections ? groupByColumn(category.sections) : [];
  return (
    <div className="flex items-stretch">
      <div className="flex flex-1 items-stretch gap-x-10 px-6">
        {columns.map((sections, idx) => {
          // A group carrying an inner grid claims width in proportion to its
          // inner column count, so every item column across the panel — the two
          // under SOLUTIONS and the one under TEAMS — ends up the same size.
          const span = Math.max(...sections.map((s) => s.innerColumns ?? 1));
          return (
            <div
              key={idx}
              className="flex flex-col"
              style={{ flex: `${span} ${span} 0%` }}
            >
              {sections.map((section, sectionIdx) => (
                <div key={section.id} className={sectionIdx > 0 ? "-mt-2" : ""}>
                  <MenuSection section={section} darkItemHover={darkItemHover} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {category.cta && <CtaCard cta={category.cta} />}
    </div>
  );
}

function MenuSection({
  section,
  darkItemHover,
}: {
  section: NavSection;
  darkItemHover: string;
}) {
  const style = section.style ?? "rich";
  const cols = section.innerColumns ?? 1;
  const gridGap = cols === 3 ? "gap-x-8" : cols === 2 ? "gap-x-10" : "";
  // Inner grids fill column-first, so the config order reads down column one
  // and then down column two — the order an editor sees in the dropdown.
  const gridStyle =
    cols > 1
      ? {
          gridAutoFlow: "column" as const,
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${Math.ceil(section.items.length / cols)}, minmax(0, 1fr))`,
        }
      : undefined;

  // Single-column sections fill the column up to a 400px cap so wider
  // dropdowns get more breathing room without items becoming unwieldy.
  const itemWidth = cols === 1 ? "w-full max-w-[400px]" : "w-full";

  // The Solutions › Teams section uses 28px icons (vs the 36px default
  // elsewhere) to keep the team-role list visually compact.
  const iconBoxWidth = section.id === "solution-teams" ? "w-7" : "w-9";

  return (
    <div className="space-y-3 py-6">
      <div className="border-b border-nav-border pb-3 font-display text-xs font-medium uppercase leading-none tracking-[1px] text-teal-dark dark:border-eng-circuit dark:text-eng-signal">
        <SectionLabel section={section} />
      </div>
      <ul className={`grid h-fit gap-y-[22px] ${gridGap}`} style={gridStyle}>
        {section.items.map((item) => (
          <li key={item.id} className={itemWidth}>
            <NavigationMenu.Link
              href={item.url || "#"}
              className={`group flex ${
                style === "rich" ? "items-start" : "items-center"
              } gap-2`}
            >
              {item.icon && (
                <div className={`mr-1 flex-none ${iconBoxWidth}`}>
                  <img
                    src={`/images/nav/icons/${item.icon}.svg`}
                    alt=""
                    className="h-auto w-full"
                  />
                </div>
              )}
              <div className={style === "rich" ? "space-y-1" : ""}>
                <p className="flex items-center gap-2 font-bold leading-tight">
                  <span className={`whitespace-nowrap text-13 text-teal-dark group-hover:text-dark dark:text-eng-bright ${darkItemHover}`}>
                    {item.label}
                  </span>
                  {item.badge && (
                    <span className="rounded-sm bg-pink pb-0.5 pl-1 pr-px pt-px text-xxxs tracking-widest-2 text-white">
                      {item.badge}
                    </span>
                  )}
                </p>
                {style === "rich" && item.description && (
                  <p className="text-13 leading-snug text-gray-80/70 group-hover:text-dark dark:text-eng-signal dark:group-hover:text-eng-bright">
                    {item.description}
                  </p>
                )}
              </div>
            </NavigationMenu.Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CtaCard({ cta }: { cta: NavCta }) {
  return (
    <div className="group/card flex w-[18rem] flex-none flex-col border-l border-nav-border bg-cream pb-7 pl-5 pr-5 pt-5 dark:border-eng-circuit dark:bg-white/5">
      {cta.image && (
        <img
          src={cta.image}
          alt=""
          className="mb-4 block w-full"
          loading="lazy"
        />
      )}
      <p className="text-base font-semibold leading-tight text-charcoal dark:text-eng-bright">
        {cta.heading}
      </p>
      <p className={`${cta.buttonText ? "mb-6" : ""} mt-1.5 text-sm font-normal leading-tight text-[#7C7976] dark:text-eng-signal`}>
        {cta.description}
      </p>
      {cta.buttonText && (
        <NavigationMenu.Link
          href={cta.url}
          className="group inline-flex items-center gap-2 font-body text-sm font-medium tracking-[-0.01em] text-charcoal transition-colors duration-200 ease-in-out hover:text-black dark:text-eng-bright dark:hover:text-white"
        >
          <span>{cta.buttonText}</span>
          <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </NavigationMenu.Link>
      )}
    </div>
  );
}
